import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import TicketsQueueSelect from "../TicketsQueueSelect";

const ALLOWED_STATUS = ["open", "pending"];
const T = "tickets.inbox.closeAllModal";

const CloseAllTicketsModal = ({ open, onClose, defaultStatus, userQueues }) => {
  const [status, setStatus] = useState("open");
  const [userId, setUserId] = useState("");
  const [queueIds, setQueueIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [count, setCount] = useState(null);
  const [capped, setCapped] = useState(false);
  const [counting, setCounting] = useState(false);
  const [closing, setClosing] = useState(false);
  // The pre-count must wait for the reset below to settle the scope, otherwise
  // it fires once with stale state and again with the reset one.
  const [scopeReady, setScopeReady] = useState(false);

  // Reset scope each time the modal opens.
  useEffect(() => {
    if (!open) {
      setScopeReady(false);
      return;
    }
    setStatus(ALLOWED_STATUS.includes(defaultStatus) ? defaultStatus : "open");
    setUserId("");
    setQueueIds((prev) => (prev.length ? [] : prev));
    setCount(null);
    setCapped(false);
    setScopeReady(true);
  }, [open, defaultStatus]);

  useEffect(() => {
    if (!open) return;
    api
      .get("/users/list")
      .then(({ data }) => setUsers(data.map((u) => ({ id: u.id, name: u.name }))))
      .catch(toastError);
  }, [open]);

  const buildBody = (extra) => ({
    status,
    ...(userId !== "" ? { userId } : {}),
    ...(queueIds.length > 0 ? { queueIds } : {}),
    ...extra,
  });

  // Pre-count whenever the scope changes.
  useEffect(() => {
    if (!open || !scopeReady) return undefined;
    let cancelled = false;
    setCounting(true);
    setCount(null);
    api
      .post("/tickets/closeAll", buildBody({ dryRun: true }))
      .then(({ data }) => {
        if (cancelled) return;
        setCount(data.total);
        setCapped(!!data.capped);
      })
      .catch((err) => !cancelled && toastError(err))
      .finally(() => !cancelled && setCounting(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, scopeReady, status, userId, queueIds]);

  const handleConfirm = async () => {
    setClosing(true);
    try {
      const { data } = await api.post("/tickets/closeAll", buildBody());
      if (data.capped) {
        toast.warn(i18n.t(`${T}.capped`, { count: data.fechados }));
      } else {
        toast.success(i18n.t(`${T}.done`, { count: data.fechados }));
      }
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setClosing(false);
    }
  };

  const disabled = closing || counting || !count;

  return (
    <Dialog open={open} onClose={closing ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{i18n.t(`${T}.title`)}</DialogTitle>
      <DialogContent>
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={i18n.t(`${T}.status`)}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          {ALLOWED_STATUS.map((s) => (
            <MenuItem key={s} value={s}>
              {i18n.t(`${T}.statusOptions.${s}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={i18n.t(`${T}.user`)}
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
        >
          <MenuItem value="">{i18n.t(`${T}.allUsers`)}</MenuItem>
          {users.map((u) => (
            <MenuItem key={u.id} value={u.id}>
              {u.name}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="textSecondary">
          {i18n.t(`${T}.queues`)}
        </Typography>
        <div style={{ marginBottom: 12 }}>
          <TicketsQueueSelect
            userQueues={userQueues}
            selectedQueueIds={queueIds}
            onChange={setQueueIds}
          />
        </div>
        <Typography variant="body1" style={{ minHeight: 24 }}>
          {counting && <CircularProgress size={16} />}
          {!counting && count === 0 && i18n.t(`${T}.empty`)}
          {!counting && count > 0 && i18n.t(`${T}.count`, { count })}
        </Typography>
        {!counting && count > 0 && capped && (
          <Typography variant="caption" color="textSecondary">
            {i18n.t(`${T}.cappedHint`)}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={closing}>
          {i18n.t(`${T}.cancel`)}
        </Button>
        <Button onClick={handleConfirm} color="primary" variant="contained" disabled={disabled}>
          {closing ? <CircularProgress size={20} /> : i18n.t(`${T}.confirm`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloseAllTicketsModal;
